
import {
  DIMENSION,
  DATA_PLATFORM_NAMESPACE,
  POLICY_SYNTAX_VERSION,
  POLICY_SYNTAX_VERSION_V2,
  POLICY_MODEL_VERSION,
  POLICY_MODEL_VERSION_V2,
  MEASURE_GROUP_POLICY_TYPE,
  MEASURE_POLICY_TYPE,
  POLICY_NAME_VALIDATION_MSG,
  POLICY_DESC_VALIDATION_MSG,
  POLICY_MODULE_VALIDATION_MSG,
  POLICY_ENTITY_VALIDATION_MSG,
  POLICY_ACTION_VALIDATION_MSG,
  POLICY_HIERARCHY_VALIDATION_MSG,
  POLICY_MEASURE_GROUP_VALIDATION_MSG,
  POLICY_NAMESPACE_VALIDATION_MSG,
  POLICY_MEASURE_VALIDATION_MSG,
} from './constants';
import { NOT_APPLICABLE } from '../../components/RoleDetails/constants';
import { IAttribute } from '../../models/Policies/PolicyContract';
import { IDataFormValues } from '../../models/Roles/RolesContracts';
import { updateDataFormError } from '../../store/role/PolicyData/dataActionCreator';
import { setEntityFormError } from '../../store/role/policyEntity/entityActionCreator';
import { setMeasuresPolicyFormError } from '../../store/role/PolicyMeasure/measureActionCreator';
import _ from 'lodash';

export const getDataPolicyExpression = (dataFormValues: IDataFormValues) => {
  const {
    module,
    entity,
    hierarchy,
    action
  } = dataFormValues
  const policyExpression = {
    version: '0.lrr1',
    permission: 'allow',
    policy_type: 'hierarchy',
    modules: module.map(module => ({ id: module.key, name: module.displayName })),
    resource: {
      name: 'dp.hierarchyDefinition',
      keys: [{ 'hierarchyDimension:': 'ITEM' }, { hierarchyId: entity.key }]
    },
    policy_rules: {
      resource: {
        name: 'dp.itemHierarchyLevelMember',
        key_definition: 'itemHierarchyInformation.hierarchyLevelId',
        value_definiton: {
          attribute: 'itemHierarchyInformation.itemHierarchyLevelMemberId',
          type: 'array'
        }
      },
      operator: 'contains',
      values: hierarchy
    },
    actions: action.map(action => action.displayName)
  };
  return JSON.stringify(policyExpression);
}
export const getEntityPolicyExpression = (entitiesFormData: any, columnAttributes: any, attributes: any) => {
  const {
    policyName,
    policyDescription,
    policyNamespace,
    policyResource,
    policyAction
  } = entitiesFormData;
  const policyExpression = {
    policyType: 'entity',
    namespace: policyNamespace,
    syntaxVersion: '1.0.0',
    displayName: policyName,
    description: policyDescription,
    resource: {
      name: policyResource,
      policies: {
        entitySecurity: {
          effect: 'allow',
          actions: [...policyAction.map((action: any) => action.name)]
        },
        attributeSecurity: [...getAttributeSecurity(columnAttributes)],
        dataSecurity: [...getDataSecurity(attributes)]
      }
    }
  };

  return JSON.stringify(policyExpression);
}

const getAttributeSecurity = (columnAttributes: any) => {
  const attributeSecurity = columnAttributes.map((a: IAttribute) => {
    return {
      attribute: a.attribute.displayName,
      effect: 'allow',
      actions: a.attributeAction && a.attributeAction.map((a: any) => a.name)
    };
  });
  return attributeSecurity;
};

export const getDataSecurity = (attributes: any) => {
  const dataSecurity = attributes.map((a: IAttribute) => {
    let value: any = a.value;
    const attrArr = a.attribute.key.split('.');
    const attr = attrArr[attrArr.length - 1];
    if (attr === 'countryCode' || attr === 'locationTypeCode') {
      value = _.uniq(value.map((item: any) => item.key || item));
    } else {
      if (Array.isArray(value) && value.length) {
        value = value.map((item: any) => item);
      } else {
        value = value.split(',')
          .map((item: any) => item.trim())
          .filter((e: any) => e) || value;
      }
    }
    return {
      effect: 'allow',
      condition: {
        operator: a.operator?.displayName,
        attribute: a.attribute.displayName,
        values: value
      },
      actions: a.valueAction && a.valueAction.map((a: any) => a.name)
    };
  });
  return dataSecurity;
};

export const getDimensionPolicyExpression = (dimensionPolicyData: any) => {
  const {
    policyName,
    policyDescription,
    dimension,
    hierarchy,
    selectAllDimension,
    members,
    action
  } = dimensionPolicyData;
  const policyExpression = {
    displayName: policyName,
    description: policyDescription,
    policyType: DIMENSION,
    namespace: DATA_PLATFORM_NAMESPACE,
    syntaxVersion: POLICY_SYNTAX_VERSION,
    modelVersion: POLICY_MODEL_VERSION,
    policy: {
      dimension: {
        dimensionId: dimension.id,
        hierarchyId: hierarchy.id,
        hierarchyFilter: {}
      },
      actions: action.map((item: any) => item.name)
    }
  };
  if (!selectAllDimension) {
    Object.keys(members).forEach((key, index, keysArr) => {
      if (index === keysArr.length - 1) { // If Last Level, Make it as list even for single selection
        policyExpression.policy.dimension.hierarchyFilter = {
          ...policyExpression.policy.dimension.hierarchyFilter,
          [key]: members[key].length > 1 ? members[key].map((item: any) => item.value) : [members[key][0].value]
        };
      } else {
        policyExpression.policy.dimension.hierarchyFilter = {
          ...policyExpression.policy.dimension.hierarchyFilter,
          [key]: members[key].length > 1 ? members[key].map((item: any) => item.value) : members[key][0] ? members[key][0].value : ""
        };
      }
    });
  } else {
    const level0Id = hierarchy?.levels[0]?.id;
    policyExpression.policy.dimension.hierarchyFilter = {
      [level0Id]: "*"
    }
  }
  return JSON.stringify(policyExpression);
}
export const convertToHierarchies = (obj: any) => {
  const hierarchies: any = [];

  for (const key in obj) {
    if (obj[key].levels && obj[key].actions) {
      const hierarchy: any = {
        id: key,
        depthAccess: obj[key].levels.id,
        actions: obj[key].actions.map((action: any) => action.name)
      };
      hierarchies.push(hierarchy);
    }
  }

  return hierarchies;
}
export const convertToHierarchyFilter = (members: any) => {
  let hierarchyFilter = {};
  Object.keys(members).forEach((key, index, keysArr) => {
    if ((index === keysArr.length - 1) || (index < keysArr.length - 1 && members[keysArr[index + 1]].length === 0)) { // If Last Level, Make it as list even for single selection
      if (members[key].length > 0) {
        hierarchyFilter = {
          ...hierarchyFilter,
          [key]: members[key].length > 1 ? members[key].map((item: any) => item.value) : [members[key][0]?.value]
        };
      }
    } else {
      hierarchyFilter = {
        ...hierarchyFilter,
        [key]: members[key][0] ? members[key][0].value : ""
      };
    }
  });
  return hierarchyFilter;
};
export const getDimensionPolicyExpressionV2 = (dimensionPolicyData: any) => {
  const {
    policyName,
    policyDescription,
    dimension,
    hierarchy,
    members,
    action,
    dimensionAction,
    hierarchyAllActions,
    attributes
  } = dimensionPolicyData;
  const hierarchies = convertToHierarchies(hierarchyAllActions);
  const hierarchyFilter = !!members ? convertToHierarchyFilter(members) : {};
  const policyExpression: any = {
    displayName: policyName,
    description: policyDescription,
    policyType: DIMENSION,
    namespace: DATA_PLATFORM_NAMESPACE,
    syntaxVersion: POLICY_SYNTAX_VERSION_V2,
    modelVersion: POLICY_MODEL_VERSION,
    policy: {
      dimension: {
        dimensionId: dimension.id,
        actions: dimensionAction.map((item: any) => item.name),
        hierarchyAccess: {},
        dataAccess: {}
      }
    }
  };
  const attributeAccess = attributes.filter((item: any) => item.attributeAction.length > 0 || item.attribute.key !== 'select');
  if (attributeAccess.length > 0) {
    policyExpression.modelVersion = POLICY_MODEL_VERSION_V2;
    policyExpression.policy.dimension.attributeAccess = attributeAccess.map((item: any) => {
      return {
        attribute: item.attribute.key,
        actions: item.attributeAction.map((action: any) => action.name)
      };
    });
  }
  if (hierarchies.length > 0) {
    policyExpression.policy.dimension.hierarchyAccess = { hierarchies };
  }
  if (Object.keys(hierarchyFilter).length > 0) {
    policyExpression.policy.dimension.dataAccess = {
      hierarchyId: hierarchy.id,
      hierarchyFilter,
      actions: action.map((item: any) => item.name)
    };
  }
  return JSON.stringify(policyExpression);
};
export const getMeasurePolicyExpression = (measuresFormData: any) => {
  const {
    policyName,
    policyDescription,
    isMeasureGroup,
    policyMeasureGroup,
    policyMeasures,
    policyAction
  } = measuresFormData;
  const policyExpression = {
    displayName: policyName,
    description: policyDescription,
    policyType: isMeasureGroup ? MEASURE_GROUP_POLICY_TYPE : MEASURE_POLICY_TYPE,
    namespace: DATA_PLATFORM_NAMESPACE,
    syntaxVersion: POLICY_SYNTAX_VERSION,
    modelVersion: POLICY_MODEL_VERSION,
    policy: {}
  }
  if (isMeasureGroup) {
    policyExpression.policy = {
      measureGroups: [...policyMeasureGroup.map((measureGroup: any) => measureGroup.id)],
      actions: [...policyAction.map((action: any) => action.name)]
    };
  } else {
    policyExpression.policy = {
      measures: {
        measureGroup: policyMeasureGroup,
        measures: [...policyMeasures.map((measures: any) => measures.id)]
      },
      actions: [...policyAction.map((action: any) => action.name)]
    };
  };

  return JSON.stringify(policyExpression);
}

/**
 * Policy for Validation methods 
 */
export const validateDataPolicy = (policyData: any, dispatch: Function, dataFormErrorState: any) => {
  if (
    policyData.name !== '' &&
    policyData.description !== '' &&
    policyData.module.length !== 0 &&
    policyData.entity.key !== 'select' &&
    policyData.hierarchy.length !== 0 &&
    policyData.action.length !== 0
  ) {
    return true;
  } else {
    let errorMessage = {};
    if (policyData.name === '') {
      errorMessage = {
        errorPolicyNameHelperText: POLICY_NAME_VALIDATION_MSG
      };
    } else if (policyData.description === '') {
      errorMessage = {
        errorPolicyDescriptionHelperText: POLICY_DESC_VALIDATION_MSG
      };
    } else if (policyData.module.length <= 0) {
      errorMessage = {
        errorPolicyModuleHelperText: POLICY_MODULE_VALIDATION_MSG
      };
    } else if (policyData.entity.key === 'select') {
      errorMessage = {
        errorPolicyEntityHelperText: POLICY_ENTITY_VALIDATION_MSG
      };
    } else if (policyData.hierarchy.length < 1) {
      errorMessage = {
        errorPolicyHierarchyHelperText: POLICY_HIERARCHY_VALIDATION_MSG
      };
    } else if (policyData.action.length <= 0) {
      errorMessage = {
        errorPolicyActionHelperText: POLICY_ACTION_VALIDATION_MSG
      };
    }
    dispatch(updateDataFormError({
      ...dataFormErrorState,
      ...errorMessage,
      error: true,
    })
    );
    return false;
  }
}

export const validateEntityForm = (entitiesFormData: any, dispatch: Function, entityFormError: any) => {
  let errors = { ...entityFormError };
  if (
    entitiesFormData.policyName !== '' &&
    entitiesFormData.policyNamespace !== '' &&
    entitiesFormData.policyDescription !== '' &&
    entitiesFormData.policyResource !== '' &&
    entitiesFormData.policyAction.length !== 0
  ) {
    return true;
  } else {
    if (entitiesFormData.policyResource === '') {
      errors = { ...errors, policyResource: POLICY_ENTITY_VALIDATION_MSG };
    }
    if (entitiesFormData.policyName === '') {
      errors = { ...errors, policyName: POLICY_NAME_VALIDATION_MSG };
    }
    if (entitiesFormData.policyDescription === '') {
      errors = { ...errors, policyDescription: POLICY_DESC_VALIDATION_MSG };
    }
    if (entitiesFormData.policyNamespace === '') {
      errors = { ...errors, policyNamespace: POLICY_NAMESPACE_VALIDATION_MSG };
    }
    if (entitiesFormData.policyAction.length === 0) {
      errors = { ...errors, policyAction: POLICY_ACTION_VALIDATION_MSG };
    }
    dispatch(setEntityFormError(errors));
    return false;
  }
};

export const validateMeasuresForm = (measuresFormData: any, dispatch: Function, measureFormError: any) => {
  let errors = { ...measureFormError };
  if (
    measuresFormData.policyName !== '' &&
    measuresFormData.policyDescription !== '' &&
    (measuresFormData.isMeasureGroup ?
      measuresFormData.policyMeasureGroup !== ''
      : measuresFormData.policyMeasures !== ''
    ) &&
    measuresFormData.policyAction.length !== 0
  ) {
    return true;
  } else {
    if (measuresFormData.isMeasureGroup) {
      if (measuresFormData.policyMeasureGroup === '') {
        errors = { ...errors, policyMeasureGroup: POLICY_MEASURE_GROUP_VALIDATION_MSG };
      }
    } else {
      if (measuresFormData.policyMeasures === '') {
        errors = { ...errors, policyMeasures: POLICY_MEASURE_VALIDATION_MSG };
      }
    }
    if (measuresFormData.policyName === '') {
      errors = { ...errors, policyName: POLICY_NAME_VALIDATION_MSG };
    }
    if (measuresFormData.policyDescription === '') {
      errors = { ...errors, policyDescription: POLICY_DESC_VALIDATION_MSG };
    }
    if (measuresFormData.policyAction.length === 0) {
      errors = { ...errors, policyAction: POLICY_ACTION_VALIDATION_MSG };
    }
    dispatch(setMeasuresPolicyFormError(errors));
    return false;
  }
};

export const getEntityPolicyRule = (policyExpression: any) => {
  const {
    namespace,
    resource: { name, policies }
  } = policyExpression;
  const { attributeSecurity, dataSecurity, entitySecurity } = policies;

  return `Namespace=${namespace || NOT_APPLICABLE}; Resource=${name}; ${entitySecurity?.effect === 'allow' ? 'Allowed' : 'Denied'
    } Actions=${entitySecurity?.actions?.toString()}; Attributes=[${attributeSecurity?.length > 0 &&
    attributeSecurity?.map(
      (item: any) =>
        `{Name=${item.attribute}; ${item.effect === 'allow' ? 'Allowed' : 'Denied'
        } Actions:${item?.actions?.toString()};}`
    )}] DataSet= [${dataSecurity?.length > 0 &&
    dataSecurity?.map(
      (item: any) =>
        `{Attribute=${item?.condition?.attribute}; ${item?.condition?.operator}=${Array.isArray(item?.condition?.values) ? item?.condition?.values.toString() : item?.condition?.values
        }; ${item.effect === 'allow' ? 'Allowed' : 'Denied'} Actions:${item?.actions?.toString()};}`
    )}]`;
};
